import React, { useState, useEffect } from "react";
import { Link, AlertCircle, CheckCircle, Plus, X } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { TriggerInput } from "./TriggerInput";

interface PingPostProvider {
  id: string;
  name: string;
  url: string;
  secretKey: string;
  isGlobal?: boolean;
}

interface SelectedProvider {
  id: string;
  url: string;
  name: string;
}

interface TriggerWebhookSectionProps {
  webhookUrl: string;
  onWebhookUrlChange: (url: string) => void;
}

// URL validation function that allows query parameters
const isValidUrl = (url: string): boolean => {
  if (!url) return true; // Empty URLs are valid (webhook is optional)

  // Trim and check for spaces
  const trimmedUrl = url.trim();
  if (trimmedUrl !== url || trimmedUrl.includes(' ')) {
    return false;
  }

  try {
    // Use URL constructor for validation
    const urlObj = new URL(url);

    // Verify protocol is http or https
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return false;
    }

    const hostname = urlObj.hostname;

    // Make sure hostname doesn't have spaces
    if (hostname.includes(' ')) {
      return false;
    }

    // Check for double dots (invalid domain)
    if (hostname.includes('..')) {
      return false;
    }

    // Allow localhost and IP addresses for development
    if (hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        /^192\.168\./.test(hostname) ||
        /^10\./.test(hostname) ||
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname)) {
      return true;
    }

    // For other hostnames, ensure they have at least one dot (valid domain)
    if (!hostname.includes('.')) {
      return false;
    }

    return true;
  } catch (error) {
    // If URL constructor throws an error, the URL is invalid
    return false;
  }
};

export const TriggerWebhookSection: React.FC<TriggerWebhookSectionProps> = ({
  webhookUrl,
  onWebhookUrlChange,
}) => {
  const { id: chatbotId } = useParams();
  const { user } = useAuth();
  const [isValid, setIsValid] = useState<boolean>(true);
  const [webhookType, setWebhookType] = useState<'pingpost' | 'custom'>('custom');
  const [selectedProviders, setSelectedProviders] = useState<SelectedProvider[]>([]);
  const [availableProviders, setAvailableProviders] = useState<PingPostProvider[]>([]);
  const [currentSelectId, setCurrentSelectId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  // Parse the webhookUrl to check if it's a JSON string containing multiple URLs
  const parseWebhookUrl = (url: string): SelectedProvider[] => {
    if (!url) return [];

    console.log('🔍 PARSING WEBHOOK URL:', {
      url,
      availableProvidersCount: availableProviders.length,
      availableProviders: availableProviders.map(p => ({ id: p.id, name: p.name }))
    });

    try {
      // Try to parse as JSON array of provider URLs
      const parsed = JSON.parse(url);
      if (Array.isArray(parsed) && parsed.every(p => p.id && p.url)) {
        console.log('📋 PARSED PROVIDERS FROM JSON:', parsed);

        // If the parsed data doesn't have names, fetch them from available providers
        const result = parsed.map(p => {
          const provider = availableProviders.find(ap => ap.id === p.id);
          const name = p.name || (provider ? provider.name : 'Unknown Provider');

          console.log(`🏷️ MAPPING PROVIDER ${p.id}:`, {
            originalName: p.name,
            foundProvider: !!provider,
            providerName: provider?.name,
            finalName: name
          });

          return {
            id: p.id,
            url: p.url,
            name: name
          };
        });

        console.log('✅ FINAL PARSED RESULT:', result);
        return result;
      }
    } catch (e) {
      // Not JSON, check if it's a single provider URL
      console.log('❌ NOT JSON, checking single provider URL');
    }

    // Check if it's a single provider URL
    const provider = availableProviders.find(p => p.url === url);
    if (provider) {
      console.log('🎯 FOUND SINGLE PROVIDER:', provider);
      return [{ id: provider.id, url: provider.url, name: provider.name }];
    }

    console.log('❌ NO PROVIDERS FOUND');
    return [];
  };

  // Serialize selected providers to a single string for storage
  const serializeProviders = (providers: SelectedProvider[]): string => {
    if (providers.length === 0) return '';
    if (providers.length === 1) return providers[0].url;
    // For multiple providers, store as JSON with id, url, and name
    return JSON.stringify(providers.map(p => ({
      id: p.id,
      url: p.url,
      name: p.name
    })));
  };

  // Load pingpost providers (both local and global)
  useEffect(() => {
    if (chatbotId && user) {
      loadProviders();
    }
  }, [chatbotId, user]);

  const loadProviders = async () => {
    setIsLoading(true);
    console.log('🔄 LOADING PROVIDERS...', { chatbotId, userId: user?.id });

    try {
      // Load local providers from database (chatbot-specific providers)
      const { data: localData, error: localError } = await supabase
        .from('pingpost_providers')
        .select('id, name, url, secret_key, is_global')
        .eq('user_id', user?.id)
        .eq('chatbot_id', chatbotId)
        .eq('is_global', false);

      if (localError && localError.code !== '42P01') {
        throw localError;
      }

      // Map local providers to the expected format
      const localProviders: PingPostProvider[] = (localData || [])
        .map(p => ({
          id: p.id,
          name: p.name,
          url: p.url,
          secretKey: p.secret_key,
          isGlobal: false
        }));

      // Load global providers from database (only enabled ones)
      const { data: globalData, error: globalError } = await supabase
        .from('pingpost_providers')
        .select('id, name, url, secret_key, is_global, is_enabled')
        .eq('user_id', user?.id)
        .eq('is_global', true)
        .eq('is_enabled', true);

      // Check if the error is due to the table not existing or column missing
      if (globalError && (globalError.code === '42P01' || globalError.message?.includes('is_enabled'))) {
        console.log('The pingpost_providers table or is_enabled column does not exist yet. Loading all global providers.');
        // Fallback: load all global providers without filtering by is_enabled
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('pingpost_providers')
          .select('id, name, url, secret_key, is_global')
          .eq('user_id', user?.id)
          .eq('is_global', true);

        if (fallbackError && fallbackError.code !== '42P01') {
          throw fallbackError;
        }

        // Map fallback global providers to the expected format
        const enabledGlobalProvidersList = (fallbackData || [])
          .map(p => ({
            id: p.id,
            name: p.name,
            url: p.url,
            secretKey: p.secret_key,
            isGlobal: true
          }));

        setAvailableProviders([...localProviders, ...enabledGlobalProvidersList]);
        return;
      } else if (globalError) {
        throw globalError;
      }

      // Map global providers to the expected format
      const enabledGlobalProvidersList = (globalData || [])
        .map(p => ({
          id: p.id,
          name: p.name,
          url: p.url,
          secretKey: p.secret_key,
          isGlobal: true
        }));

      // Combine local and enabled global providers
      setAvailableProviders([...localProviders, ...enabledGlobalProvidersList]);
    } catch (error) {
      console.error('Error loading providers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize webhook type and selected providers based on webhookUrl
  useEffect(() => {
    if (!webhookUrl) {
      setSelectedProviders([]);
      return;
    }

    // Only try to parse if we have available providers loaded
    if (availableProviders.length > 0) {
      const selected = parseWebhookUrl(webhookUrl);

      if (selected.length > 0) {
        setSelectedProviders(selected);
        setWebhookType('pingpost');
      } else if (webhookUrl) {
        // If there's a URL but it's not a provider, it's a custom URL
        setWebhookType('custom');
        setSelectedProviders([]);
      }
    }
  }, [webhookUrl, availableProviders]);

  // Add a provider to the selected list
  const handleAddProvider = () => {
    if (!currentSelectId || selectedProviders.some(p => p.id === currentSelectId)) return;

    const provider = availableProviders.find(p => p.id === currentSelectId);
    if (provider) {
      const newProvider = { id: provider.id, url: provider.url, name: provider.name };
      const updatedProviders = [...selectedProviders, newProvider];
      setSelectedProviders(updatedProviders);
      setCurrentSelectId('');

      // Update the webhook URL with the serialized providers
      const serialized = serializeProviders(updatedProviders);
      onWebhookUrlChange(serialized);
    }
  };

  // Remove a provider from the selected list
  const handleRemoveProvider = (providerId: string) => {
    const updated = selectedProviders.filter(p => p.id !== providerId);
    setSelectedProviders(updated);

    // Update the webhook URL with the serialized providers or empty string
    if (updated.length === 0) {
      onWebhookUrlChange('');
    } else {
      const serialized = serializeProviders(updated);
      onWebhookUrlChange(serialized);
    }
  };

  // Handle webhook type change
  const handleWebhookTypeChange = (value: 'pingpost' | 'custom') => {
    if (value === webhookType) return; // No change

    setWebhookType(value);

    if (value === 'pingpost') {
      // If switching to pingpost, keep the current URL empty until a provider is selected
      // This ensures we don't lose any custom URL when switching back
      setCurrentSelectId('');
      if (selectedProviders.length > 0) {
        const serialized = serializeProviders(selectedProviders);
        onWebhookUrlChange(serialized);
      } else {
        // No providers selected yet, clear the URL
        onWebhookUrlChange('');
      }
    } else if (value === 'custom') {
      // If switching to custom, clear any provider selections but don't change URL yet
      setSelectedProviders([]);
      // Only clear the URL if it was a provider URL
      if (parseWebhookUrl(webhookUrl).length > 0) {
        onWebhookUrlChange('');
      }
    }
  };

  // Get provider name by ID
  const getProviderName = (id: string): string => {
    const provider = availableProviders.find(p => p.id === id);
    return provider ? provider.name : 'Unknown Provider';
  };

  // Validate when URL changes
  useEffect(() => {
    if (webhookType === 'custom') {
      setIsValid(isValidUrl(webhookUrl));
    } else if (webhookType === 'pingpost') {
      // PingPost providers are always valid when selected through the UI
      setIsValid(true);
    }
  }, [webhookUrl, webhookType]);
  return (
    <div className="space-y-4">
      <div className="flex items-center">
        <Link className="h-4 w-4 mr-1" />
        <h4 className="text-sm font-medium text-gray-700">Webhook Integration</h4>
      </div>

      <RadioGroup
        value={webhookType}
        onValueChange={(value) => handleWebhookTypeChange(value as 'pingpost' | 'custom')}
        className="space-y-3"
      >
        <div className="flex items-center space-x-2">
          <RadioGroupItem value="pingpost" id="pingpost" />
          <Label htmlFor="pingpost" className="font-medium cursor-pointer">
            PingPost Providers
            {availableProviders.length > 0 && (
              <Badge variant="outline" className="ml-2 text-xs">
                {availableProviders.length} {availableProviders.length === 1 ? 'available' : 'available'}
              </Badge>
            )}
          </Label>
        </div>

        {webhookType === 'pingpost' && (
          <div className="ml-6 space-y-4">
            {availableProviders.length > 0 ? (
              <>
                {/* Selected providers list */}
                {selectedProviders.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-500">Selected Providers:</Label>
                    <div className="space-y-2">
                      {selectedProviders.map((provider) => (
                        <div key={provider.id} className="flex items-center justify-between p-2 bg-muted/30 rounded border">
                          <div className="flex items-center">
                            <span className="text-sm font-medium">{getProviderName(provider.id)}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-gray-500 hover:text-red-500"
                            onClick={() => handleRemoveProvider(provider.id)}
                            type="button"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add provider selector */}
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-xs text-gray-500 mb-1 block">Add Provider:</Label>
                    <Select value={currentSelectId} onValueChange={setCurrentSelectId}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a provider" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableProviders
                          .filter(p => !selectedProviders.some(sp => sp.id === p.id))
                          .map((provider) => (
                            <SelectItem key={provider.id} value={provider.id}>
                              {provider.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-1"
                    onClick={handleAddProvider}
                    disabled={!currentSelectId || selectedProviders.some(p => p.id === currentSelectId)}
                    type="button"
                  >
                    <Plus className="h-3 w-3" />
                    Add
                  </Button>
                </div>

                {selectedProviders.length === 0 && (
                  <div className="text-sm text-blue-600 bg-blue-50 p-3 rounded-md flex items-start">
                    <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs">Select one or more providers to send data to when this trigger rule is activated.</p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-amber-600 bg-amber-50 p-3 rounded-md flex items-start">
                <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">No PingPost providers configured</p>
                  <p className="text-xs mt-1">Go to Integrations tab to add PingPost providers first.</p>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center space-x-2">
          <RadioGroupItem value="custom" id="custom" />
          <Label htmlFor="custom" className="font-medium cursor-pointer">Custom URL</Label>
        </div>

        {webhookType === 'custom' && (
          <div className="ml-6 space-y-2">
            <div className="relative">
              <TriggerInput
                initialValue={webhookUrl}
                onSave={onWebhookUrlChange}
                className={`w-full text-sm p-2 pr-9 border rounded ${!isValid && webhookUrl ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : webhookUrl ? 'border-green-300 focus:ring-green-500 focus:border-green-500' : ''}`}
                placeholder="https://example.com/webhook?param=value"
                validationFn={isValidUrl}
                showValidation={true}
              />
              {webhookUrl && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  {isValid ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-red-500" />
                  )}
                </div>
              )}
            </div>
            {!isValid && webhookUrl && (
              <p className="text-xs text-red-500">
                Please enter a valid URL (must start with http:// or https:// and cannot contain spaces)
              </p>
            )}
          </div>
        )}
      </RadioGroup>

      <p className="text-xs text-gray-500 mt-1">
        When all conditions are met, user responses will be sent to {webhookType === 'pingpost' && selectedProviders.length > 1 ? 'these URLs' : 'this URL'}.
        {webhookType === 'custom' && ' Query parameters are supported (e.g., ?user_id=[VARIABLE])'}
      </p>

      {/* Debug information - remove in production */}
      {/* <div className="mt-4 p-2 border border-dashed border-gray-300 rounded text-xs">
        <p><strong>Current webhookUrl:</strong> {webhookUrl || 'empty'}</p>
        <p><strong>Type:</strong> {webhookType}</p>
        <p><strong>Selected providers:</strong> {selectedProviders.length}</p>
      </div> */}
    </div>
  );
};
